import React, { useState } from 'react';
import { Upload, X, FileText, Eye, Download } from 'lucide-react';
import { uploadAPI } from '../../../utils/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../utils/i18n';
import { useDialog } from '../../../components/Dialog';
import './StepStyles.css';

const Step1Upload = ({ formData, setFormData }) => {
  const { language } = useAuth();
  const { t } = useTranslation(language);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [viewingFile, setViewingFile] = useState(null);
  const { showError, DialogComponent } = useDialog();

  // Resize image to max width/height while maintaining aspect ratio
  const resizeImage = (file, maxWidth = 1920, maxHeight = 1920, quality = 0.85) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();

        img.onload = () => {
          // Calculate new dimensions
          let width = img.width;
          let height = img.height;

          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = width * ratio;
            height = height * ratio;
          }

          // Create canvas and draw resized image
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob
          canvas.toBlob(
            (blob) => {
              if (blob) {
                // Create new file with same name
                const resizedFile = new File([blob], file.name, {
                  type: file.type,
                  lastModified: Date.now(),
                });
                resolve(resizedFile);
              } else {
                reject(new Error('Canvas to Blob conversion failed'));
              }
            },
            file.type,
            quality
          );
        };

        img.onerror = () => reject(new Error('Image load failed'));
        img.src = e.target.result;
      };

      reader.onerror = () => reject(new Error('FileReader failed'));
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);

    if (files.length === 0) return;

    console.log('📁 Files selected:', files.map(f => ({
      name: f.name,
      type: f.type,
      size: f.size
    })));

    // Validate file types
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));

    if (invalidFiles.length > 0) {
      showError(t('invalidFileType') || `Invalid file type. Only images (JPEG, PNG, GIF, WEBP) and PDF are allowed.`);
      return;
    }

    // Check for empty files
    const emptyFiles = files.filter(file => file.size === 0);
    if (emptyFiles.length > 0) {
      console.error('❌ Empty files detected:', emptyFiles.map(f => f.name));
      showError(t('emptyFileError') || 'Some files are empty. Please try again.');
      return;
    }

    setUploading(true);

    try {
      // Process files: resize images, ensure PDFs are properly loaded
      const processedFiles = await Promise.all(
        files.map(async (file) => {
          // Check if it's an image
          if (file.type.startsWith('image/')) {
            try {
              const resizedFile = await resizeImage(file);
              console.log(`✅ Resized ${file.name}: ${(file.size / 1024).toFixed(0)}KB → ${(resizedFile.size / 1024).toFixed(0)}KB`);
              return resizedFile;
            } catch (error) {
              console.error('Resize failed, using original:', error);
              return file;
            }
          }
          // For PDF, ensure it's properly loaded (iOS Safari fix)
          if (file.type === 'application/pdf') {
            console.log(`📄 PDF file: ${file.name} (${(file.size / 1024).toFixed(0)}KB)`);
            // Read file to ensure it's loaded in memory
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = (e) => {
                // Create new File from ArrayBuffer to ensure data is loaded
                const blob = new Blob([e.target.result], { type: 'application/pdf' });
                const newFile = new File([blob], file.name, {
                  type: 'application/pdf',
                  lastModified: file.lastModified || Date.now()
                });
                console.log(`✅ PDF loaded: ${newFile.name} (${(newFile.size / 1024).toFixed(0)}KB)`);
                resolve(newFile);
              };
              reader.onerror = () => {
                console.error('❌ Failed to read PDF:', file.name);
                reject(new Error('Failed to read PDF file'));
              };
              reader.readAsArrayBuffer(file);
            });
          }
          // Other files
          return file;
        })
      );

      // Upload processed files
      const uploadPromises = processedFiles.map((file) =>
        uploadAPI.uploadFile(file, (progress) => {
          setUploadProgress(progress);
        })
      );

      const responses = await Promise.all(uploadPromises);
      const uploadedFiles = responses.map((res) => res.data);

      setFormData((prev) => ({
        ...prev,
        documents: [...(prev.documents || []), ...uploadedFiles],
      }));
    } catch (error) {
      console.error('Upload failed:', error);
      showError(t('uploadFailed'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index),
    }));
  };

  return (
    <div className="step-container">
      <DialogComponent />

      <div className="upload-zone">
        <input
          type="file"
          id="file-upload"
          multiple
          accept="image/*,.pdf"
          onChange={handleFileChange}
          className="file-input"
          disabled={uploading}
        />

        <label htmlFor="file-upload" className="upload-label">
          {uploading ? (
            <div className="upload-progress">
              <div className="spinner"></div>
              <p>{t('uploading')} {uploadProgress}%</p>
            </div>
          ) : (
            <>
              <Upload size={48} />
              <p className="upload-text">{t('clickOrDrag')}</p>
              <p className="upload-hint">{t('supportedFormats')}</p>
            </>
          )}
        </label>
      </div>

      {formData.documents && formData.documents.length > 0 && (
        <div className="uploaded-files">
          <h3 className="files-title">Đã upload ({formData.documents.length}):</h3>
          <div className="files-list">
            {formData.documents.map((doc, index) => {
              const fileUrl = doc.url || doc.fileUrl;
              const fileName = doc.filename || doc.fileName || `File ${index + 1}`;
              const fileType = doc.format || doc.fileType || doc.type || '';

              return (
                <div key={index} className="file-item">
                  <FileText size={20} />
                  <span className="file-name">{fileName}</span>
                  {fileUrl && (
                    <button
                      type="button"
                      className="btn-view"
                      onClick={() => setViewingFile({ url: fileUrl, name: fileName, type: fileType })}
                      title={t('clickToView') || 'Xem file'}
                    >
                      <Eye size={16} />
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn-remove"
                    onClick={() => handleRemoveFile(index)}
                  >
                    <X size={16} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* File Viewer Modal */}
      {viewingFile && (
        <div className="file-viewer-overlay" onClick={() => setViewingFile(null)}>
          <div className="file-viewer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="file-viewer-header">
              <h3 className="file-viewer-title">{viewingFile.name}</h3>
              <div className="file-viewer-actions">
                <a
                  href={viewingFile.url}
                  download={viewingFile.name}
                  className="btn-download"
                  title={t('download') || 'Tải xuống'}
                >
                  <Download size={20} />
                </a>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setViewingFile(null)}
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="file-viewer-content">
              {viewingFile.type === 'pdf' || viewingFile.url.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={viewingFile.url}
                  className="file-viewer-iframe"
                  title={viewingFile.name}
                />
              ) : (
                <img
                  src={viewingFile.url}
                  alt={viewingFile.name}
                  className="file-viewer-image"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {(!formData.documents || formData.documents.length === 0) && (
        <div className="step-info warning">
          ⚠️ {t('uploadAtLeastOne')}
        </div>
      )}
    </div>
  );
};

export default Step1Upload;
